/**
    * @description      : 
    * @author           : 
    * @group            : 
    * @created          : 02/09/2025 - 21:16:12
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 02/09/2025
    * - Author          : 
    * - Modification    : 
**/
import React, { useEffect, useState } from "react";
import images from "../../images/index";
import { Button } from "react-bootstrap";
import { Image } from "react-bootstrap";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { api } from "../../api/transaction";
import { useTranslation } from "react-i18next";
import TermsAndConditionsModal from "./TermsAndConditionsModal";

interface FullReportProps {
  SimulationId: string;
}

const FullReport: React.FC<FullReportProps> = ({ SimulationId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams(); // Use the hook at component level
  const [scrollPosition, setScrollPosition] = useState(0);
  const { t } = useTranslation("fullReport");
  // New state for the terms modal
  const [showTermsModal, setShowTermsModal] = useState(false);

  // Extract projectId from params or location state
  const projectId = params.projectId || location.state?.projectId;

  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [scrollPosition]);

  const handlePurchaseClick = () => {
    setShowTermsModal(true);
  };

  // Now getClientSecret uses projectId from the component scope
  const getClientSecret = async () => {
    try {
      if (!projectId) {
        console.error("Project ID is missing");
        return;
      }
      
      if (!SimulationId) {
        console.error("Simulation ID is missing");
        return;
      }
      
      // Call API with all required data
      const fetchData = await api.fetchClientSecret({
        amount: 1900,
        projectId: projectId,
        simulationId: SimulationId
      });
      
      // Navigate to payment page with all necessary information
      navigate("/payment", {
        state: {
          paymentIntents_ID: fetchData?.data?.paymentIntents_ID,
          clientSecret: fetchData?.data?.clientSecret,
          projectId: projectId,
          SimulationId: SimulationId,
        },
      });
    } catch (error) {
      console.error("Error getting client secret:", error);
    }
  };

  return (
    <>
      <TermsAndConditionsModal 
        show={showTermsModal}
        onHide={() => setShowTermsModal(false)}
        onAccept={() => {
          setShowTermsModal(false);
          getClientSecret();
        }}
      />
      <div
        className="FullReportCard p-4"
        style={{ position: "sticky", top: "15px" }}
      >
        <p className="fullReport">{t("title")}</p>
        <div className="d-flex PO-card-data my-2">
          <Image src={images.CheckImage} width={18} height={18} />
          <small className="mx-2">
            <b>{t("features.duration", "Precise Glare Timing:")}</b> {t("features.durationText", "Identify the exact dates and times when glare occurs throughout the year.")}
          </small>
        </div>
        <div className="d-flex PO-card-data my-2">
          <Image src={images.CheckImage} width={18} height={18} />
          <small className="mx-2">
            <b>{t("features.affectedAreas", "Customized Area Analysis:")}</b> {t("features.affectedAreasText", "Select specific locations and zones to assess precise glare impact on surrounding properties")}
          </small>
        </div>
        <div className="d-flex PO-card-data my-2">
          <Image src={images.CheckImage} width={18} height={18} />
          <small className="mx-2">
            <b>{t("features.intensity", "Detailed Intensity Measurements:")}</b> {t("features.intensityText", "Understand the severity of glare events with quantitative intensity metrics")}
          </small>
        </div>
        <div className="d-flex PO-card-data my-2">
          <Image src={images.CheckImage} width={18} height={18} />
          <small className="mx-2">
            <b>{t("features.localization", "Pinpoint Reflection Sources:")}</b> {t("features.localizationText", "Locate the exact panels causing reflection issues to implement targeted solutions")}
          </small>
        </div>
        <div className="d-flex PO-card-data my-2">
          <Image src={images.CheckImage} width={18} height={18} />
          <small className="mx-2">
            <b>{t("features.representation", "Visual Timeline Representation:")}</b> {t("features.representationText", "You also get access to an Excel file containing the simulation results for additional assessment")}
          </small>
        </div>
        <div className="d-flex PO-card-data my-2">
          <Image src={images.CheckImage} width={18} height={18} />
          <small className="mx-2">
            <b>{t("pricing.label", "Amount:")}</b> {t("pricing.text", "The amount you have to pay is 19€.")}
          </small>
        </div>
        <div className="fullProfileButton d-flex flex-column align-items-center">
          <Button
            onClick={handlePurchaseClick}
            className="my-2 d-block PO-card-purchaseFullReport"
          >
            {t("actions.purchase", "Purchase full report")}
          </Button>
          <Link to="/pdf">
            <Button variant="none" className="PO-card-seeExample">
              {t("actions.seeExample", "See example")}
            </Button>
          </Link>
        </div>
      </div>
    </>
  ); 
};

export default FullReport;